import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

import styles from './styles.module.scss';
import { AlertManager } from '../Alert/index.js';
import AppBar from '../AppBar/index.js';
import Button from '../Button/index.ts';
import Form from '../Form/index.js';
import Icon from '../Icon/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';
import useClickOutside from '../utils/useClickOutside/index.ts';
import useRequest from '../utils/useRequest/index.ts';

export const PLACEMENTS = ['top', 'center'];
export const VARIANTS = ['default'];
const ICONS = {
  CLOSE: faTimes
};
const { overflow: bodyOverflow } = document.body.style;
const Modal = ({
  onChange,
  onClose,
  onOpen,
  isOpen: isOpenFromProps,
  isOpenByDefault,
  isDismissable,
  children,
  elementToRenderTo,
  className,
  variant,
  width: widthFromProps,
  rightHeader: rightRaw,
  leftHeader: leftRaw,
  footer: footerRaw,
  headerIcon,
  title,
  hasHeader,
  hasFooter,
  placement,
  activator: activatorRaw,
  action: actionRaw,
  actionLabel,
  shouldCloseOnAction,
  isLoading: isLoadingFromProps,
  isFullscreen,
  // eslint-disable-next-line react/prop-types
  style,
  ...props
}) => {
  const {
    value: isOpen,
    set: setOpen,
    toggle: toggleOpen,
    setTrue: open,
    setFalse: onDoneClosing
  } = useBoolean(isOpenByDefault, {
    overrideWith: isOpenFromProps,
    onChange(isActive) {
      if (isActive) {
        onOpen();
      } else {
        onClose();
      }

      onChange(isActive);
    }
  });
  const [isClosing, setIsClosing] = useState(false);
  const close = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onDoneClosing();
      setIsClosing(false);
    }, 200);
  }, [setIsClosing, onDoneClosing]);
  const width = isFullscreen ? '100%' : widthFromProps;
  let heightToSubtract = isFullscreen ? 0 : 128;

  const {
    value: isLoading,
    setTrue: startLoading,
    setFalse: stopLoading
  } = useBoolean(false, {
    overrideWith: isLoadingFromProps
  });
  const [hasInputErrors, setInputErrors] = useState(false);
  const childrenEl = useMemo(
    () =>
      typeof children === 'function'
        ? children({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close,
            isLoading,
            startLoading,
            stopLoading
          })
        : children,
    [children, isOpen, toggleOpen, setOpen, open, close, isLoading, startLoading, stopLoading]
  );
  const right = useMemo(
    () =>
      typeof rightRaw === 'function'
        ? rightRaw({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close,
            isLoading,
            startLoading,
            stopLoading,
            closeButton: (
              <Button
                icon={ICONS.CLOSE}
                onClick={close}
                className={styles['aglmd-modal__close-icon']}
                variant={['round', 'flat']}
              />
            )
          })
        : rightRaw,
    [rightRaw, isOpen, toggleOpen, setOpen, open, close, isLoading, startLoading, stopLoading]
  );
  const left = useMemo(
    () =>
      typeof leftRaw === 'function'
        ? leftRaw({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close,
            headerIcon,
            title,
            isLoading,
            startLoading,
            stopLoading
          })
        : leftRaw,
    [
      leftRaw,
      isOpen,
      toggleOpen,
      setOpen,
      open,
      close,
      headerIcon,
      title,
      isLoading,
      startLoading,
      stopLoading
    ]
  );
  const node = useClickOutside(close, { active: isOpen && isDismissable });
  const activator = useMemo(
    () =>
      typeof activatorRaw === 'function'
        ? activatorRaw({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close,
            isLoading,
            startLoading,
            stopLoading
          })
        : activatorRaw,
    [activatorRaw, isOpen, toggleOpen, setOpen, open, close, isLoading, startLoading, stopLoading]
  );
  useEffect(() => {
    const closeOnEsc = e => {
      if (isOpen && e.keyCode === 27) {
        close();
      }
    };

    window.addEventListener('keydown', closeOnEsc);

    return () => window.removeEventListener('keydown', closeOnEsc);
  }, [close, isOpen]);

  // scroll lock the background page
  useLayoutEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = bodyOverflow;
    }
    return () => {
      if (isOpen) {
        document.body.style.overflow = bodyOverflow;
      }
    };
  }, [isOpen]);

  let header;
  let footer;

  if (hasHeader) {
    heightToSubtract += 64;
    header = (
      <AppBar
        className={addVariants('aglmd-modal__header', styles)}
        left={left}
        right={right}
        isLoading={isLoading}
      />
    );
  }

  if (hasFooter) {
    heightToSubtract += 64;
    footer = (
      <Footer
        actionRaw={actionRaw}
        startLoading={startLoading}
        stopLoading={stopLoading}
        close={close}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        setOpen={setOpen}
        open={open}
        actionLabel={actionLabel}
        isLoading={isLoading}
        hasInputErrors={hasInputErrors}
        shouldCloseOnAction={shouldCloseOnAction}
      >
        {footerRaw}
      </Footer>
    );
  }

  const modalContent =
    isOpen &&
    ReactDOM.createPortal(
      <div className={addVariants('aglmd-modal__wrapper', styles, isClosing ? 'closing' : '')}>
        <div
          className={classnames(
            addVariants('aglmd-modal', styles, variant, placement, isFullscreen && 'fullscreen'),
            className
          )}
          ref={node}
          style={{
            width: typeof width === 'string' ? width : `${width}px`,
            maxHeight: `calc(100vh - ${isFullscreen ? 0 : 128}px)`,
            height: isFullscreen ? '100vh' : 'auto',
            ...(style || {})
          }}
          {...props}
        >
          <div className={addVariants('aglmd-modal__content__wrapper', styles)}>
            {header}
            <AlertManager>
              <div
                className={addVariants('aglmd-modal__content', styles, variant)}
                style={{
                  maxHeight: `calc(100vh - ${heightToSubtract}px)`,
                  height: isFullscreen ? `calc(100vh - ${heightToSubtract}px)` : 'auto'
                }}
              >
                {childrenEl}
              </div>
              {footer}
            </AlertManager>
          </div>
        </div>
      </div>,
      elementToRenderTo
    );

  return (
    <Form onChange={setInputErrors}>
      <>
        {activator}
        {modalContent}
      </>
    </Form>
  );
};

const FooterDefault = ({ action, actionLabel, close, isLoading, hasInputErrors }) => {
  if (!actionLabel) {
    return null;
  }

  return (
    <>
      <Button className={addVariants('aglmd-modal__footer__cancel', styles)} onClick={close}>
        Cancel
      </Button>
      <Button
        className={addVariants('aglmd-modal__footer__action', styles)}
        color="blue"
        onClick={action}
        isDisabled={isLoading || hasInputErrors}
      >
        {actionLabel}
      </Button>
    </>
  );
};

const Footer = ({
  actionRaw,
  startLoading,
  stopLoading,
  close,
  isOpen,
  toggleOpen,
  setOpen,
  open,
  actionLabel,
  isLoading,
  hasInputErrors,
  shouldCloseOnAction,
  children: footer
  /* eslint-enabled react/prop-types */
}) => {
  const didUnMount = useRef();
  useEffect(() => {
    didUnMount.current = false;
    return () => {
      didUnMount.current = true;
    };
  });
  const action = useRequest(async ev => {
    const call = actionRaw(ev);

    if (call && call.then) {
      startLoading();
      await Promise.resolve(call)
        .catch(e => {
          stopLoading();
          throw e;
        })
        .then(() => !didUnMount.current && stopLoading());
    }

    if (shouldCloseOnAction && !didUnMount.current) {
      close();
    }
  });

  return (
    <div className={addVariants('aglmd-modal__footer', styles)}>
      {typeof footer === 'function'
        ? footer({
            isOpen,
            toggleOpen,
            setOpen,
            open,
            close,
            actionLabel,
            action,
            isLoading,
            hasInputErrors
          })
        : footer}
    </div>
  );
};

Modal.propTypes = {
  /**
   * What to render that activates the modal
   */
  activator: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * Fires on Modal close
   */
  onClose: PropTypes.func,
  /**
   * Fires on Modal open
   */
  onOpen: PropTypes.func,
  /**
   * Fires when modal opens or closes
   */
  onChange: PropTypes.func,
  /**
   * Controls the state of the modal, state must be handled by the caller
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the modal starts out open
   */
  isOpenByDefault: PropTypes.bool,
  /**
   * The inner content function or element
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * Whether the Modal is dismissable by clicking off of it
   */
  isDismissable: PropTypes.bool,
  /**
   * The DOM element selector or element to mount the Modal to
   */
  elementToRenderTo: PropTypes.any,
  className: PropTypes.string,
  /**
   * Decides the variant
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * The width to set for the Modal
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * The right portion (component or function) of the header
   */
  rightHeader: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.bool]),
  /**
   * The left portion (component or function) of the header
   */
  leftHeader: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.bool]),
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The header icon to display in the left header
   */
  headerIcon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]),
  /**
   * The title string of the modal
   */
  title: PropTypes.string,
  /**
   * Fires when the default footers action fires
   */
  action: PropTypes.func,
  /**
   * When action is resolved default is to close the modal
   */
  shouldCloseOnAction: PropTypes.bool,
  /**
   * The text to use for the default action of the footer
   */
  actionLabel: PropTypes.string,
  /**
   * Whether to show the builtin header
   */
  hasHeader: PropTypes.bool,
  /**
   * Whether to show the builtin footer
   */
  hasFooter: PropTypes.bool,
  /**
   * Where the modal is placed on screen
   */
  placement: PropTypes.oneOf(PLACEMENTS),
  /**
   * Whether to show the loading indicator when an action is loading
   */
  isLoading: PropTypes.bool,
  /**
   * Whether the modal takes up the full screen
   */
  isFullscreen: PropTypes.bool
};
Modal.defaultProps = {
  onClose: () => 0,
  onOpen: () => 0,
  onChange: () => 0,
  isOpen: null,
  className: '',
  isOpenByDefault: false,
  elementToRenderTo: document.body,
  isDismissable: false,
  hasHeader: true,
  hasFooter: true,
  headerIcon: null,
  action: () => 0,
  actionLabel: '',
  footer: FooterDefault,
  title: '',
  width: 600,

  rightHeader: ({ closeButton }) => closeButton,

  leftHeader: ({ headerIcon, title }) => (
    <div className={addVariants('aglmd-modal__header__title', styles)}>
      {headerIcon && (
        <Icon icon={headerIcon} className={addVariants('aglmd-modal__header__icon', styles)} />
      )}
      {title}
    </div>
  ),
  placement: 'top',
  variant: 'default',
  activator: null,
  shouldCloseOnAction: true,
  isLoading: null,
  isFullscreen: false
};
export default Modal;
